/* File: FaceTagOverlay.jsx
 * Path: ananunadm/src/components/media/FaceTagOverlay.jsx
*/
import { useEffect, useRef, useState } from "react";
import { Loader, Popover, Select, Text } from "@mantine/core";
import { computeHighlightBox } from "@/helpers/faceHighlightBox";
import styles from "./FaceTagOverlay.module.css";

export const NEW_PERSON_PREFIX = "__new_person__:";

const personIdOf = (face) => (typeof face.personId === "object" ? face.personId?._id : face.personId);
const personNameOf = (face) => (typeof face.personId === "object" ? face.personId?.name : null) || "Unknown";

/**
 * Tappable rectangles over each auto-detected face on the CURRENT lightbox
 * slide, letting the viewer correct/set who a face belongs to in place.
 * Positions are only trustworthy at zoom === 1 (see MediaLightbox), since
 * that's the one state where the fit-math here matches the browser's own
 * object-fit: contain sizing of the underlying <img> without needing to also
 * track pan offsets.
 */
export default function FaceTagOverlay({
	slide,
	zoom = 1,
	visibleCount,
	showLabels = false,
	animated = false,
	personOptions,
	onRequestPersonOptions,
	openFaceId,
	onOpenFace,
	savingFaceId,
	onAssign
}) {
	const containerRef = useRef(null);
	const [containerSize, setContainerSize] = useState(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;
			setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const faces = Array.isArray(slide?.faces) ? slide.faces : [];
	const visibleFaces = faces.slice(0, visibleCount === undefined ? faces.length : Math.max(0, visibleCount));
	const imgWidth = Number(slide?.width) || 0;
	const imgHeight = Number(slide?.height) || 0;
	const showBoxes = zoom <= 1.01 && containerSize && imgWidth > 0 && imgHeight > 0 && visibleFaces.length > 0;

	let imageRect = null;
	if (showBoxes) {
		const scale = Math.min(containerSize.width / imgWidth, containerSize.height / imgHeight);
		const displayWidth = imgWidth * scale;
		const displayHeight = imgHeight * scale;
		imageRect = {
			left: (containerSize.width - displayWidth) / 2,
			top: (containerSize.height - displayHeight) / 2,
			width: displayWidth,
			height: displayHeight
		};
	}

	return (
		<div ref={containerRef} className={styles.overlay} aria-live={showLabels ? "polite" : undefined}>
			{showBoxes && visibleFaces.map((face) => {
				const box = computeHighlightBox(face.boundingBox, imgWidth, imgHeight);
				if (!box) return null;

				const isOpen = openFaceId === face._id;
				const isSaving = savingFaceId === face._id;
				const name = personNameOf(face);
				const confidence = Number(face.confidence);
				const confidenceText = Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : "";
				const renderedTop = imageRect.top + box.y * imageRect.height;
				const placeLabelBelow = renderedTop < 42;

				return (
					<Popover
						key={face._id}
						opened={isOpen}
						onClose={() => onOpenFace(null)}
						withinPortal
						// The lightbox itself renders in a portal at z-index 9999 (see
						// yet-another-react-lightbox's .yarl__portal); without a higher
						// z-index here the popover is present in the DOM but visually
						// and interactively buried under the lightbox.
						zIndex={10000}
						position="bottom"
						shadow="md"
						trapFocus
					>
						<Popover.Target>
							<button
								type="button"
								className={`${styles.faceBox} ${showLabels ? styles.discoveryBox : ""} ${animated ? styles.animated : ""}`}
								style={{
									left: imageRect.left + box.x * imageRect.width,
									top: imageRect.top + box.y * imageRect.height,
									width: box.width * imageRect.width,
									height: box.height * imageRect.height
								}}
								title={name}
								aria-label={`Detected person ${name}${confidenceText ? `, ${confidenceText} confidence` : ""}`}
								onClick={(event) => {
									event.stopPropagation();
									onOpenFace(isOpen ? null : face._id);
									if (!isOpen) onRequestPersonOptions();
								}}
							>
								{showLabels && (
									<span className={`${styles.faceLabel} ${placeLabelBelow ? styles.labelBelow : ""}`}>
										<span>{name}</span>
										{confidenceText && <span className={styles.confidence}>{confidenceText}</span>}
									</span>
								)}
							</button>
						</Popover.Target>
						<Popover.Dropdown className={styles.dropdown} onClick={(event) => event.stopPropagation()}>
							<Text size="xs" color="dimmed" mb={4}>
								Currently: {personNameOf(face)}
							</Text>
							<Select
								size="sm"
								placeholder="Search or add a name"
								data={personOptions || []}
								searchable
								creatable
								autoFocus
								zIndex={10000}
								disabled={!personOptions || isSaving}
								rightSection={(!personOptions || isSaving) ? <Loader size={14} /> : null}
								getCreateLabel={(query) => `+ Add "${query}"`}
								shouldCreate={(query, data) => query.trim().length > 0 && !data.some((item) => item.label.toLowerCase() === query.trim().toLowerCase())}
								onCreate={(query) => {
									const item = { value: `${NEW_PERSON_PREFIX}${query.trim()}`, label: query.trim() };
									onRequestPersonOptions(item);
									return item;
								}}
								value={personIdOf(face) || null}
								onChange={(value) => value && onAssign(face, value)}
								nothingFound="No matches"
							/>
						</Popover.Dropdown>
					</Popover>
				);
			})}
		</div>
	);
}
